import React from 'react';
import { useApolloClient } from '@apollo/client';

import DelayedAccessAlert, {
  Location
} from '@buyside/components/presentation/AccessControl/delayedAccessAlert';
import InfoTable from '@buyside/components/presentation/IdeaShowPage/InfoTable/infoTable';
import ResearchItem, {
  ResearchTypes
} from '@buyside/components/presentation/ResearchItem/researchItem';
import ResearchItemLoading from '@buyside/components/presentation/ResearchItem/researchItemLoading';

import { useRelatedIdeasQuery, IdeaCurrentUserQuery } from '@interfaces/graphql-typings';

const IdeaCurrentUser = require('buyside/graphql/queries/ideaShowPage/IdeaCurrentUser');

import styles from './relatedIdeasStyles.scss';

const RelatedIdeas: React.FC<{
  ideaId: string;
  isPublic: boolean;
  loading: boolean;
}> = ({ ideaId, isPublic, loading: isLoading }): React.ReactElement => {
  const { data, loading, error } = useRelatedIdeasQuery({ variables: { ideaId } });

  const client = useApolloClient();

  const cachedCurrentUser = client.readQuery<IdeaCurrentUserQuery>({
    query: IdeaCurrentUser,
    variables: { ideaId }
  })?.currentUser;

  if (isLoading || loading)
    return (
      <InfoTable title="Related Research">
        {(): React.ReactElement => <ResearchItemLoading times={5} narrow />}
      </InfoTable>
    );

  if (error || !data) throw error;

  const {
    idea: { relatedIdeas }
  } = data;

  const canViewUserDetails = !isPublic && Boolean(cachedCurrentUser?.canViewUserDetails);
  const currentUserId = cachedCurrentUser?.id;

  return (
    <InfoTable title="Related Research">
      {(): React.ReactElement => (
        <div>
          {relatedIdeas.length ? (
            <>
              <DelayedAccessAlert
                id={Location.ideaRelatedIdeas}
                customStyles={styles.alertPersistent}
                isPublic={isPublic}
              />
              {relatedIdeas.map(idea => {
                const ideaAuthorId = idea?.user?.id;

                return (
                  idea &&
                  ideaAuthorId && (
                    <div className={styles.ideaCardWrap} key={idea.id}>
                      <ResearchItem
                        {...idea}
                        researchType={ResearchTypes.IDEA}
                        isPublic={isPublic}
                        narrow
                        showUserDetails={
                          canViewUserDetails ||
                          ideaAuthorId === currentUserId ||
                          (!isPublic && idea?.isNonBuyside)
                        }
                      />
                    </div>
                  )
                );
              })}
            </>
          ) : (
            <span className={styles.noRelatedResearch}>
              No related research available. Check back later.
            </span>
          )}
        </div>
      )}
    </InfoTable>
  );
};

export default RelatedIdeas;
